'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { IconStudio } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { buildShowcaseVideos } from '@repo/studio-components-showcase-strip/showcase-strip'
import { SeedanceVideoItem } from './seedance-video-item'

const LFS_URL = process.env.NEXT_PUBLIC_LFS_URL ?? ''
const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''
const AI_VIDEO_GENERATOR_URL = `${STUDIO_BASE_URL}/ai-video-generator`

const SHOWCASE_VIDEOS = buildShowcaseVideos(`${LFS_URL}/studio/video/seedance-2-0`, 'seedance')
// Loop the nine real clips so the widest layout (8 columns × 2 rows) stays filled.
const GRID_VIDEOS = [...SHOWCASE_VIDEOS, ...SHOWCASE_VIDEOS.slice(0, 8).toReversed()].slice(0, 16)

// Columns grow with the viewport (3 → 8), so each breakpoint fits a whole number of
// two-row grids. Tiles past a breakpoint's two-row capacity are hidden to cap at two rows.
const TILE_VISIBILITY_BY_INDEX = [
  '',
  '',
  '',
  '',
  '',
  '',
  'hidden sm:block',
  'hidden sm:block',
  'hidden md:block',
  'hidden md:block',
  'hidden lg:block',
  'hidden lg:block',
  'hidden xl:block',
  'hidden xl:block',
  'hidden 2xl:block',
  'hidden 2xl:block',
]

interface SeedanceShowcaseBlockProps {
  badgeLabel: string
  title: string
  subtitle: string
  ctaLabel: string
  className?: string
}

export function SeedanceShowcaseBlock({
  badgeLabel,
  title,
  subtitle,
  ctaLabel,
  className = '',
}: SeedanceShowcaseBlockProps) {
  return (
    <section className={`rounded-4 bg-cf-white p-6 lg:p-8 ${className}`}>
      <div className="flex items-center gap-2">
        <IconStudio className="size-5 text-cf-purple-900" />
        <h2 className="font-poppins text-xl lg:text-2xl font-bold text-cf-purple-900">{title}</h2>
        <span className="rounded-full bg-cf-purple-100 px-2.5 py-0.5 text-xs font-semibold text-cf-purple-900">
          {badgeLabel}
        </span>
      </div>
      <p className="mt-1.5 text-sm text-cf-grey-500">{subtitle}</p>
      <div className="relative mt-5">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8">
          {GRID_VIDEOS.map((video, index) => (
            <SeedanceVideoItem
              key={`${video.id}-${index}`}
              src={video.previewUrl}
              tryItLabel={ctaLabel}
              viewLabel={ctaLabel}
              showHoverOverlay={false}
              className={`w-full ${TILE_VISIBILITY_BY_INDEX[index] ?? ''}`}
              aspectClassName="aspect-[9/16]"
              showDuration
            />
          ))}
        </div>
        {/* White scrim fading the bottom row into the card so the CTA reads on a clean backdrop. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-cf-white via-cf-white/85 to-transparent" />
        <Button
          variant="primary"
          size="regular"
          asChild
          className="absolute bottom-2 left-1/2 -translate-x-1/2 gap-1 shadow-lg"
        >
          <Link href={AI_VIDEO_GENERATOR_URL} target="_blank" rel="noopener noreferrer">
            <IconStudio className="!size-4" />
            <span>{ctaLabel}</span>
          </Link>
        </Button>
      </div>
    </section>
  )
}
